/**
 * MOCK/TEMPORARY — Lyrixa share links.
 *
 * ============================================================================
 * RESOLUTION IS NOT REAL, and it is the whole point of a share link. `getShared`
 * looks the slug up in the mock's songs table (./mockCatalogue), which lives in this
 * browser's memory — so it can only ever resolve songs this browser already has. A
 * recipient — someone the link was actually sent to — has an empty table and sees "not
 * found". This is the same shape of gap as licenseApi.verifyLicense, and the same fix: a
 * server-side record.
 *
 * The table is not the improvement it might look like: it replaced a `songs` PARAMETER
 * (Batch 7 §1.6), which was worse because it let the caller decide what a public link could
 * resolve to. Same browser either way. See `getShared`.
 *
 * Two endpoints are needed, and they are the two functions below:
 *   POST /songs/:id/share      issues (or returns) a share token for a song
 *   GET  /share/:slug          PUBLIC, no auth — resolves a token to a payload
 *
 * The second must be public, because a recipient is not signed in. It must also
 * return only what the owner chose to expose, which is why SharedSong below is a
 * separate, narrower type than Song rather than the record itself: `prompt`,
 * `creditsUsed` and `versions` remain the owner's business, while the lyric sheet is
 * intentionally included for the shared page.
 *
 * It now carries `creatorName` as well (Batch 7 §5) — the credit line, and the one field on
 * this payload that is about a person. `creatorId` is not on it. See the field.
 *
 * AND WHICH SONGS IT MAY RESOLVE AT ALL is the other half of that boundary, added with
 * `Song.visibility`. A share link is not a way around a visibility decision:
 *
 *   public    resolves. The owner published it.
 *   unlisted  resolves. This is the ONLY thing `unlisted` does — reachable by link,
 *             absent from every listing. See types/domain.SongVisibility.
 *   private   does NOT resolve. `isLinkAccessible` below is the rule, and it is applied
 *             in getShared rather than in the page, so a private song never reaches the
 *             public payload in the first place.
 *
 * WHICH MEANS A LINK RESOLVES ONLY FOR A SONG SOMEBODY ACTUALLY PUBLISHED, and only in the
 * browser that published it. The seed library is entirely private (data/songs.ts), so a
 * fresh session resolves nothing; the Library's "Make discoverable" action sets `'public'`
 * and links to that song then resolve. Nothing sets `'unlisted'` yet — there is no
 * share-link-only control — so that branch of `isLinkAccessible` is currently unreachable
 * from the UI and is kept because the value is part of the contract.
 *
 * A private song returning null is a truthful consequence of the rule, not a bug in it: the
 * alternative is resolving links to songs their owner never chose to share, which is the
 * leak the two fields exist to prevent.
 *
 * WHAT THE BACKEND STILL OWES `unlisted`. Two things, neither of which the client can
 * provide:
 *   1. The rule server-side. This module runs in the browser; the real GET /share/:slug
 *      must refuse a private song itself, because a payload that arrives has leaked
 *      whatever the client does with it afterwards.
 *   2. A revocable token. The slug here is DERIVED from the song id (lib/share.ts), so
 *      every song is permanently addressable and "unshare" is impossible — flipping a
 *      song back to private is currently the only revocation, and a real design needs a
 *      stored token that can be rotated. See lib/share.ts and docs/api-contract.md §22.
 * ============================================================================
 */
import { USE_MOCK_API } from '../config';
import { artworkSeedFor } from '../lib/artwork';
import { shareSlug, songShareUrl } from '../lib/share';
import type { LyricSectionTiming, LyricTiming, Song } from '../types/domain';
import { http } from './http';
import { assertMock, mockDelay } from './mock';
import { mockCatalogueSongs } from './mockCatalogue';
import { toApiError } from './errors';

/**
 * Whether a direct link may resolve to this song. NOT the same rule as discovery.
 *
 * TWO DIFFERENT QUESTIONS, DELIBERATELY TWO DIFFERENT PREDICATES. Discovery asks "may this
 * be *listed*" (lib/discovery.isPubliclyDiscoverable: ready AND public); this asks "may
 * this be *reached by someone holding its link*". `unlisted` is the value that separates
 * them, and collapsing the two would either hide public songs from their own links or
 * publish unlisted ones into the catalogue.
 *
 * `status` is not consulted. A link to a song still generating resolves to its page and
 * shows no player, which is the same thing the owner sees; there is no audio to withhold.
 * Visibility is the owner's decision and is the only thing gated here.
 */
export function isLinkAccessible(song: Song): boolean {
  return song.visibility === 'public' || song.visibility === 'unlisted';
}

/**
 * What a share link issues.
 *
 * `url` is what gets copied; `slug` is kept alongside it so the caller can show or
 * store the short form without re-parsing the URL.
 */
export interface ShareLink {
  slug: string;
  url: string;
}

/**
 * The public view of a shared song — deliberately narrower than `Song`.
 *
 * Everything here is information the recipient needs to know what they are
 * listening to. Everything omitted is either private (the prompt that produced it,
 * what it cost, its version history) or meaningless to a stranger (download count). The
 * lyric sheet is included because the shared page now shows it, and the page's Love action
 * needs the viewer's own Love flag plus the aggregate Love count.
 */
export interface SharedSong {
  /** The slug it was resolved from, for the "shared link" line on the page. */
  slug: string;
  title: string;
  genre: string;
  length: string;
  bpm: number;
  mood: string;
  language: string;
  /** Display date the track was created. */
  date: string;
  /** Which model produced it — attribution, not a private detail. */
  aiModel: string;
  /** The lyric sheet shown on the shared page. */
  lyrics: string;
  /** Real line timings for the shared lyric sheet, when this song has them. */
  lyricTimings?: LyricTiming[];
  /** Real section timings for the shared lyric sheet, when this song has them. */
  lyricSectionTimings?: LyricSectionTiming[];
  /** Whether the CURRENT VIEWER has Loved this song. */
  favorite: boolean;
  /** Loves from OTHER accounts, excluding `favorite` exactly as on Song. */
  loveCount?: number;
  /**
   * The creator's display name, for the page's "Created by …" line (Batch 7 §5).
   *
   * ==========================================================================
   * THE ONE FIELD ABOUT A PERSON ON A PUBLIC PAYLOAD, so it is the one to be careful with.
   *
   * It is here because a shared page with no credit is the surface where the omission is
   * worst: a recipient arrives from a link with no account, no catalogue and no context, and
   * the creator is the single thing they most need to be told. Every other attribution
   * surface reads `Song.creatorName` directly; this type is narrower than `Song` on purpose,
   * so the field has to be projected explicitly — which is the right shape, because it makes
   * publishing a name a decision somebody wrote down rather than a consequence of a spread.
   *
   * `creatorId` IS DELIBERATELY NOT HERE. The name is a display string and nothing else
   * (docs/api-contract.md §6: "not an email, not a username that doubles as a login
   * identifier, not an id that appears in another account's URLs"); the id is an account
   * handle that appears in authenticated routes, and an unauthenticated endpoint handing out
   * account ids gives a stranger something to address. The page needs a name to print and
   * has no use for an id it cannot resolve.
   *
   * OPTIONAL, and absent means unknown rather than anonymous — the same rule as on `Song`.
   * lib/attribution renders nothing at all for an absent name: no "Created by Unknown", and
   * on this page especially no fallback to whoever is looking, because whoever is looking is
   * not the creator. The seed songs carry no creator, so no line renders on them.
   *
   * FIRST NAME ONLY IS APPLIED AT RENDER, not here. The full string crosses the boundary
   * because the trimming decision belongs in lib/attribution where every surface shares it
   * — see the module header on why the wire keeps what the backend sent.
   * ==========================================================================
   */
  creatorName?: string;
  /**
   * Gradient seed, so the shared page can draw the same artwork.
   *
   * Always present, and deliberately resolved through lib/artwork rather than copied
   * from `song.artworkSeed`: the owner's library falls back to the song id when no
   * artwork has been applied, and a shared page that skipped that fallback showed a
   * stranger a different cover than the sender was looking at.
   */
  artworkSeed: string;
  coverStyle?: string;
  coverUrl?: string | null;
  /**
   * Null while generation is simulated, which is why the shared page offers no
   * playback: there is no audio to stream. See the page's own notice.
   */
  audioUrl?: string | null;
}

/** Projects a Song onto its public shape. Nothing else may cross this boundary. */
function toSharedSong(song: Song): SharedSong {
  return {
    slug: shareSlug(song.id),
    title: song.title,
    genre: song.genre,
    length: song.length,
    bpm: song.bpm,
    mood: song.mood,
    language: song.language,
    date: song.date,
    aiModel: song.aiModel,
    lyrics: song.lyrics,
    lyricTimings: song.lyricTimings,
    lyricSectionTimings: song.lyricSectionTimings,
    favorite: song.favorite,
    loveCount: song.loveCount,
    // The display name only — never `creatorId`. See the field, and note that this is a
    // projection rather than a spread precisely so a new field on `Song` cannot arrive here
    // by accident.
    creatorName: song.creatorName,
    artworkSeed: artworkSeedFor(song),
    coverStyle: song.coverStyle,
    coverUrl: song.coverUrl ?? null,
    audioUrl: song.audioUrl ?? null,
  };
}

export const shareApi = {
  /**
   * MOCK: POST /songs/:id/share
   *
   * Derives the link rather than issuing one, so it is stable across reloads
   * without persistence. The real endpoint mints a random token, stores it against
   * the song, and can revoke it — see lib/share.ts on why derivation is not an
   * acceptable long-term design.
   */
  async createShareLink(songId: string): Promise<ShareLink> {
    if (!USE_MOCK_API) {
      await http.post<void>(`/songs/${songId}/shares`);
      return { slug: shareSlug(songId), url: songShareUrl(songId) };
    }
    assertMock('shareApi.createShareLink');
    return mockDelay({ slug: shareSlug(songId), url: songShareUrl(songId) });
  },

  /**
   * MOCK: GET /share/:slug  (PUBLIC — no auth)
   *
   * ==========================================================================
   * NO LIBRARY PARAMETER (Batch 7 §1.6). `getShared(slug, songs)` searched a library the
   * CALLER supplied, which made a public resolution answerable by the browser: the visibility
   * gate below could only ever be applied to whatever the caller chose to hand over. The
   * lookup now goes to the mock's songs table (./mockCatalogue), so the argument that used to
   * mark this as fake is gone and the signature is the real endpoint's.
   *
   * It is still not a real resolution, and for the reason the header gives rather than this
   * one: the table lives in the same browser as the sender, so a recipient — a different
   * browser, with no library — still cannot resolve anything. That is a missing SERVER, not a
   * misplaced argument.
   * ==========================================================================
   *
   * A PRIVATE SONG RESOLVES TO NULL, indistinguishably from a slug that matches nothing.
   * Same payload, same not-found page, no "this song is private" message — because saying
   * that would confirm the song exists, which is itself a disclosure to someone guessing
   * slugs. The caller cannot tell the two cases apart and does not need to.
   */
  async getShared(slug: string): Promise<SharedSong | null> {
    if (!USE_MOCK_API) {
      try {
        return await http.get<SharedSong>(`/share/${slug}`, { auth: false });
      } catch (err) {
        const error = toApiError(err);
        if (error.kind === 'notFound') return null;
        throw error;
      }
    }
    assertMock('shareApi.getShared');
    const wanted = slug.toLowerCase();
    const song = mockCatalogueSongs().find((s) => shareSlug(s.id) === wanted);
    /*
      The visibility check happens HERE, at the boundary, not in routes/Share.tsx. A page
      that fetched the full payload and then declined to draw it would still have handed a
      private song to a public surface — and it is the payload that is the leak, not the
      pixels. See `isLinkAccessible` and the header on what the server must repeat.
    */
    return mockDelay(song && isLinkAccessible(song) ? toSharedSong(song) : null);
  },
};
